import { CellDisabled, SelectWrap } from "@/pages/Requirement/style";
import { RequirementColumn } from "@/pages/Requirement/type";
import SText from "@/styles/SText";
import { Product } from "@/types/product";
import { Flex, Popover, Tag } from "antd";
import { Dispatch, FC, ReactNode, SetStateAction, useMemo } from "react";
import { useTranslation } from "react-i18next";
interface ReplaceBomSelectorProps {
  row: number;
  bomSubs: Array<RequirementColumn>;
  setBomSubs: Dispatch<SetStateAction<RequirementColumn[]>>;
  optMethord: "MIXED" | "REPLACED";
}
const ReplaceBomSelector: FC<ReplaceBomSelectorProps> = ({ row, bomSubs, setBomSubs, optMethord }) => {
  const { t } = useTranslation();

  const { options, defaultValue } = useMemo(() => {
    const currentProduct = bomSubs[row]?.product;
    if (!currentProduct) return { options: [], defaultValue: undefined };

    // REPLACED 模式: options = [当前产品, ...reqs]
    // MIXED 模式: options = reqs (排除自身)
    const baseOptions = optMethord === "REPLACED"
      ? [...bomSubs[row].reqs, { ...bomSubs[row], reqs: [] }]
      : bomSubs[row].reqs;

    const options = baseOptions
      .sort((a, b) => (a.product as Product).id.localeCompare((b.product as Product).id))
      .map((item) => ({
        key: item.product?.id,
        value: item.product?.id,
        info: item,
        label: (
          <Popover
            showArrow={false}
            content={() => getContent(item.product)}
          >
            <Flex justify="space-between">
              <SText text={item.product?.name ?? ""} />
              <Tag color="#87d068">{item.product?.code}</Tag>
            </Flex>
          </Popover>
        ),
      }));

    // MIXED 模式: 返回所有直接子替代料（通过 replaceParentId 匹配且在 reqs 列表中）
    // REPLACED 模式: 返回已选择的替代料ID，如果没有则返回当前产品ID
    let defaultValue;
    if (optMethord === "MIXED") {
      defaultValue = bomSubs
        .filter(bom =>
          bom.replaceParentId === currentProduct.id &&
          bomSubs[row].reqs.some(req => req.product?.id === bom.product?.id)
        )
        .map(bom => bom.product?.id);
    } else {
      // const replacedItem = bomSubs.find(bom =>
      //   bom.replaceParentId === currentProduct.id &&
      //   bomSubs[row].reqs.some(req => req.product?.id === bom.product?.id)
      // );
      defaultValue = bomSubs[row].product.id;
    }

    return { options, defaultValue };
  }, [bomSubs, row, optMethord]);

  const getContent: (item: Product | null) => ReactNode = (item) => {
    return (
      <div>
        <div>{t("product.code")}: {item?.code}</div>
        <div>{t("product.name")}: {item?.name}</div>
        <div>{t("product.brand1")}: {item?.model?.toLocaleUpperCase()}</div>
        <div>{t("product.brand2")}: {item?.t1?.toLocaleUpperCase()}</div>
        <div>{t("product.uom")}: {item?.uom?.toLocaleUpperCase()}</div>
        <div>{t("product.cbm")}: {item?.cbm}</div>
      </div>
    );
  };

  const onChangeMixed = (selectedOptions: Array<{ value: string; info: RequirementColumn }>) => {
    const currentProduct = bomSubs[row]?.product;
    if (!currentProduct) return;

    const replaceParentId = currentProduct.id;

    // 1. 先移除所有属于当前行的子替代料行（parentId 匹配的行）
    const remainingBoms = bomSubs.filter(bom => bom.replaceParentId !== replaceParentId);

    // 2. 构建新选择的替代料行
    const newReplItems = selectedOptions.map(option => ({
      ...option.info,
      bomItemId: option.info.bomItemId,
      bomReplId: currentProduct.id,
      replaceParentId,
      reqs: [],
      bomRequireCount: 0,
      baseQuantity: 0,
      attritionRate: 0,
      useQuantity: 0,
      useAmount: 0,
      extCount: 0,
    }) as RequirementColumn);

    // 3. 在当前行后面插入新的替代料行
    const currentRowIndex = remainingBoms.findIndex(
      bom => bom.bomItemId === bomSubs[row]?.bomItemId
    );

    if (currentRowIndex === -1) {
      setBomSubs([...remainingBoms, ...newReplItems]);
    } else {
      const newBomSubs = [
        ...remainingBoms.slice(0, currentRowIndex),
        { ...bomSubs[row], beReplaced: !!selectedOptions.length },
        ...newReplItems,
        ...remainingBoms.slice(currentRowIndex + 1)
      ];
      setBomSubs(newBomSubs);
    }
  }
  const onChangeReplaced = (option: { value: string; info: RequirementColumn }) => {
    const currentProduct = bomSubs[row]?.product;
    if (!currentProduct) return;

    // 如果选中的是当前产品本身，不做任何操作
    if (option.value === currentProduct.id) return;
    const newBomSubs = [...bomSubs];

    // 获取当前行的 reqs（例如 A 的 reqs = [A, B, C, D]）
    const currentReqs = [...bomSubs[row].reqs, { ...bomSubs[row], reqs: [] }]
      .filter(req => req.product.id !== option.info.product?.id)
      .map(req => ({ ...req, beReplaced: false, replaceParentId: undefined, reqs: [] }))

    const newReplacedItem = {
      ...option.info,
      bomItemId: option.info.bomItemId,
      bomReplId: option.info.bomReplId || currentProduct.id,
      reqs: [...currentReqs], // 创建新数组
      beReplaced: false,
      replaceParentId: undefined
    } as RequirementColumn;

    // 替换 bomSubs 中的当前行

    newBomSubs[row] = newReplacedItem;
    setBomSubs([...newBomSubs]);
  }

  if (bomSubs[row].reqs.length > 0) {
    return (
      <SelectWrap
        key={row}
        mode={optMethord === "REPLACED" ? undefined : "multiple"}
        // defaultValue={[bomSubs[row]?.product?.id ?? ""]}
        filterOption={false}
        popupMatchSelectWidth={false}
        onChange={(_value, option: any) => {
          optMethord === "MIXED" && onChangeMixed(option as Array<{ value: string; info: RequirementColumn }>)
          optMethord === "REPLACED" && onChangeReplaced(option as { value: string; info: RequirementColumn })
        }}
        placeholder={t("components.plProduct")}
        options={options}
        allowClear={optMethord === "MIXED"}
        value={defaultValue}
      />
    );
  }

  return <CellDisabled>{t("requirement.noRepl")}</CellDisabled>;
};
export default ReplaceBomSelector;
